import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, FileText } from "lucide-react";
import { useLang } from "../i18n";
import { useAuth } from "../auth";
import api from "../api";
import { track } from "../telemetry";

export const PaymentCancel = () => {
  const { t } = useLang();
  useEffect(() => { track("payment_cancel"); }, []);
  return (
    <main className="pt-16 min-h-screen flex items-center justify-center px-5" data-testid="payment-cancel-page">
      <div className="text-center max-w-md">
        <XCircle className="w-14 h-14 text-mute mx-auto mb-6" />
        <h1 className="font-display font-bold text-3xl mb-4">{t("pay_cancel_title")}</h1>
        <p className="text-mist mb-8">{t("pay_cancel_sub")}</p>
        <Link to="/storie" data-testid="payment-cancel-back" className="inline-flex rounded-full border border-ember/50 px-7 py-3 text-sm font-semibold uppercase tracking-wider text-ember hover:bg-ember hover:text-black transition-colors">
          {t("pay_ok_back")}
        </Link>
      </div>
    </main>
  );
};

const PaymentSuccess = () => {
  const { t } = useLang();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [codes, setCodes] = useState([]);
  const [copied, setCopied] = useState(null);
  const [invBusy, setInvBusy] = useState(false);

  const requestInvoice = async () => {
    const sid = params.get("session_id");
    setInvBusy(true);
    try {
      const { data } = await api.post(`/payments/${sid}/invoice-request`);
      navigate(`/fattura/${data.token}`);
    } catch { /* già richiesto o non trovato */ } finally {
      setInvBusy(false);
    }
  };

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus(data);
          track("payment_ok", data.lookup_key || "");
          checkAuth();
          try {
            const { data: c } = await api.get(`/payments/${sid}/codes`);
            setCodes(c);
          } catch { /* nessun codice (es. sblocco diretto) */ }
          return;
        }
      } catch { /* retry */ }
      if (++tries < 12) setTimeout(poll, 2000);
    };
    poll();
  }, [params, checkAuth]);

  const isUnlock = status?.lookup_key === "unlock_full";

  return (
    <main className="pt-16 min-h-screen flex items-center justify-center px-5" data-testid="payment-success-page">
      <div className="text-center max-w-md">
        <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-6" />
        <h1 className="font-display font-bold text-3xl mb-4" data-testid="payment-success-title">{t("pay_ok_title")}</h1>
        <p className="text-mist mb-8" data-testid="payment-success-message">
          {status ? (isUnlock ? t("pay_ok_unlock") : t("pay_ok_donation")) : t("pay_ok_wait")}
        </p>
        {codes.length > 0 && (
          <div className="rounded-2xl border border-gold/40 bg-gold/5 p-6 mb-8 text-left" data-testid="payment-codes">
            <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold mb-4">{t("codes_title")}</p>
            <div className="space-y-3">
              {codes.map((c) => (
                <div key={c.code} className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-mono2 font-bold tracking-widest text-gold" data-testid={`code-${c.code}`}>{c.code}</p>
                    <p className="text-[11px] text-mute">
                      {c.gift ? t("code_gift") : `${t("code_yours")} · ${c.grant === "chapters8" ? t("code_grant_chapters8") : t("code_grant_full")}`}
                    </p>
                  </div>
                  <button
                    data-testid={`copy-${c.code}`}
                    onClick={() => { navigator.clipboard?.writeText(c.code); setCopied(c.code); setTimeout(() => setCopied(null), 1500); }}
                    className="rounded-full border border-gold/50 px-4 py-1.5 text-[10px] font-mono2 tracking-widest uppercase text-gold hover:bg-gold hover:text-black transition-colors shrink-0"
                  >
                    {copied === c.code ? t("code_copied") : t("code_copy")}
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-mute mt-4">{t("code_howto")}</p>
          </div>
        )}
        <Link to="/storie" data-testid="payment-success-back"
          className="inline-flex rounded-full bg-ember px-7 py-3 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
          {t("pay_ok_back")}
        </Link>
        {status?.payment_status === "paid" && user && (
          <button onClick={requestInvoice} disabled={invBusy} data-testid="payment-invoice-request"
            className="mt-5 mx-auto flex items-center gap-2 font-mono2 text-[11px] tracking-[0.2em] uppercase text-mute hover:text-ember transition-colors">
            <FileText className="w-3.5 h-3.5" /> {t("pay_ok_invoice")}
          </button>
        )}
      </div>
    </main>
  );
};

export default PaymentSuccess;
